import { useState } from 'react'
import { Loader2, Check, X, Hammer, Send } from 'lucide-react'
import { useAdminImprovements, useUpdateMelhoriaAnalysis, useChangeMelhoriaStatus } from '@/hooks/useNaiDesk'
import type { NdImprovement } from '@/hooks/useNaiDesk'
import { MelStatusBadge } from '../NaiDeskPage'

export default function MelhoriasAdmin() {
  const [moduleSel, setModule] = useState('')
  const [status, setStatus] = useState('')
  const { data, isLoading } = useAdminImprovements({ module: moduleSel, melhoria_status: status })
  const [editing, setEditing] = useState<NdImprovement | null>(null)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Solicitações de Melhoria — Admin</h1>
        <p className="text-sm text-gray-500">Análise técnica, aprovação e roadmap</p>
      </header>

      <div className="flex gap-3 mb-4">
        <select value={moduleSel} onChange={e => setModule(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <option value="">Todos os módulos</option>
          {['IDM', 'Authoritative Repository', 'Access Control', 'Data Privacy', 'Third Party', 'Task Hub', 'Evo ESG', 'GRCWay'].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <option value="">Todos os status</option>
          <option value="pending_analysis">Em análise</option>
          <option value="approved">Aprovadas</option>
          <option value="development">Em desenvolvimento</option>
          <option value="published">Publicadas</option>
          <option value="rejected">Rejeitadas</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-600 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-left">Assunto</th>
              <th className="px-4 py-3 text-left">Módulo</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Horas</th>
              <th className="px-4 py-3 text-left">Roadmap</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {data?.map(m => (
              <tr key={m.ticket_id} className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-4 py-3 font-mono text-gray-500">#{m.ticket_number}</td>
                <td className="px-4 py-3 text-xs">{m.user_name}</td>
                <td className="px-4 py-3 max-w-xs truncate">{m.subject}</td>
                <td className="px-4 py-3 text-xs">{m.module || '—'}</td>
                <td className="px-4 py-3"><MelStatusBadge status={m.melhoria_status} /></td>
                <td className="px-4 py-3 text-xs">{m.estimated_hours || '—'}</td>
                <td className="px-4 py-3 text-xs">{m.include_in_roadmap ? '✓' : '—'}</td>
                <td className="px-4 py-3"><button onClick={() => setEditing(m)} className="text-blue-600 hover:underline text-xs">Analisar</button></td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Nenhuma melhoria.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <AnalysisModal item={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

function AnalysisModal({ item, onClose }: { item: NdImprovement; onClose: () => void }) {
  const update = useUpdateMelhoriaAnalysis()
  const change = useChangeMelhoriaStatus()
  const [hours, setHours] = useState(item.estimated_hours?.toString() || '')
  const [cost, setCost] = useState(item.estimated_cost?.toString() || '')
  const [roi, setRoi] = useState(item.roi_forecast || '')
  const [period, setPeriod] = useState(item.roi_period || '')
  const [planned, setPlanned] = useState(item.planned_publish_date || '')
  const [actual, setActual] = useState(item.actual_publish_date || '')
  const [roadmap, setRoadmap] = useState(item.include_in_roadmap)
  const [reason, setReason] = useState(item.rejection_reason || '')
  const [notes, setNotes] = useState(item.analysis_notes || '')

  const save = () => update.mutate({
    ticketId: item.ticket_id,
    payload: {
      estimated_hours: hours ? Number(hours) : null,
      estimated_cost: cost ? Number(cost) : null,
      roi_forecast: roi || null, roi_period: period || null,
      planned_publish_date: planned || null, actual_publish_date: actual || null,
      include_in_roadmap: roadmap, rejection_reason: reason || null, analysis_notes: notes || null,
    } as any,
  })

  const act = (action: 'approve' | 'reject' | 'development' | 'publish') => {
    change.mutate({ ticketId: item.ticket_id, action }, { onSuccess: onClose })
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <header className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <span className="text-xs font-mono text-gray-500">#{item.ticket_number}</span>
          <h3 className="font-semibold text-lg">{item.subject}</h3>
          <p className="text-xs text-gray-500 mt-1">por {item.user_name} · módulo {item.module}</p>
        </header>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Horas estimadas"><input type="number" value={hours} onChange={e => setHours(e.target.value)} className="input" /></Field>
            <Field label="Custo estimado (R$)"><input type="number" value={cost} onChange={e => setCost(e.target.value)} className="input" /></Field>
          </div>
          <Field label="ROI previsto"><input value={roi} onChange={e => setRoi(e.target.value)} className="input" /></Field>
          <Field label="Período de retorno"><input value={period} onChange={e => setPeriod(e.target.value)} className="input" placeholder="ex: 6 meses" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data prevista"><input type="date" value={planned} onChange={e => setPlanned(e.target.value)} className="input" /></Field>
            <Field label="Data efetiva"><input type="date" value={actual} onChange={e => setActual(e.target.value)} className="input" /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={roadmap} onChange={e => setRoadmap(e.target.checked)} />
            Incluir no Roadmap público
          </label>
          <Field label="Notas internas"><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="input" /></Field>
          <Field label="Motivo de rejeição"><textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="input" /></Field>
        </div>
        <footer className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex flex-wrap gap-2 justify-between">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => act('approve')} className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold inline-flex items-center gap-1"><Check className="h-3 w-3" />Aprovar</button>
            <button onClick={() => act('reject')} className="px-3 py-1.5 rounded bg-red-600 text-white text-xs font-semibold inline-flex items-center gap-1"><X className="h-3 w-3" />Rejeitar</button>
            <button onClick={() => act('development')} className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-semibold inline-flex items-center gap-1"><Hammer className="h-3 w-3" />Iniciar dev</button>
            <button onClick={() => act('publish')} className="px-3 py-1.5 rounded bg-purple-600 text-white text-xs font-semibold inline-flex items-center gap-1"><Send className="h-3 w-3" />Publicar</button>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs">Fechar</button>
            <button onClick={save} disabled={update.isPending} className="px-3 py-1.5 rounded bg-gray-900 text-white text-xs font-semibold">{update.isPending ? 'Salvando...' : 'Salvar análise'}</button>
          </div>
        </footer>
      </div>
      <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border-radius: 0.5rem; border: 1px solid rgb(229 231 235); background: white; font-size: 0.875rem; } .dark .input { border-color: rgb(55 65 81); background: rgb(31 41 55); color: white; }`}</style>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  )
}
